import React, { useState, useEffect, useRef, useMemo } from 'react';
import Service from '../../../services/graphQlServices';
import { SUGGESTED_CREW_QUERY } from '../../../controllers/crewAvailabilityGql';
import { Tooltip } from 'antd';
import '../../../assets/scss/crewSuggestions.scss';

const RANK_LABELS = ['#1', '#2', '#3'];

const CrewSuggestions = ({ eventId, vendorId, assignedUserIds = [], unassignedRoles = [] }) => {
	const [suggestions, setSuggestions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const [expandedRoles, setExpandedRoles] = useState({});
	const searchRef = useRef(null);

	const rolesKey = useMemo(() => unassignedRoles.map(r => `${r.role}_${r.subRole}`).join(','), [unassignedRoles]);
	useEffect(() => {
		if (!eventId || unassignedRoles.length === 0) return;
		fetchSuggestions();
	}, [eventId, rolesKey]);

	const fetchSuggestions = async () => {
		setLoading(true);
		setError(false);
		// Only query for roles that have unassigned slots
		const roles = unassignedRoles.map(r => ({ role: r.role, subRole: r.subRole }));
		const [success, response] = await Service.query(
			SUGGESTED_CREW_QUERY,
			{ eventId, roles },
			vendorId,
			localStorage.getItem('usertoken'),
			'vendors'
		);
		setLoading(false);
		if (success && response.data?.suggestedCrew) {
			setSuggestions(response.data.suggestedCrew);
			// Auto-expand first role group
			const first = response.data.suggestedCrew[0];
			if (first) {
				setExpandedRoles({ [`${first.role}-${first.subRole}`]: true });
			}
		} else {
			setError(true);
		}
	};

	if (error || (!loading && suggestions.length === 0)) return null;

	const getScoreColor = (score) => {
		if (score >= 80) return '#2f9b2c';
		if (score >= 50) return '#ff9800';
		return '#f8532b';
	};

	const getStatusLabel = (status) => {
		if (status === 'AVAILABLE') return 'Available';
		if (status === 'PARTIALLY_AVAILABLE') return 'Partial';
		return 'Busy';
	};

	const toggleRole = (key) => {
		setExpandedRoles(prev => ({ ...prev, [key]: !prev[key] }));
	};

	const renderRoleGroup = (group) => {
		const key = `${group.role}-${group.subRole}`;
		const isExpanded = expandedRoles[key];
		const roleName = `${group.role} · ${group.subRole}`;

		let sorted = (group.suggestions || [])
			.filter(s => !assignedUserIds.includes(s.userId));

		// Sort by fitScore descending and assign original rank before search filtering
		sorted.sort((a, b) => (b.fitScore || 0) - (a.fitScore || 0));
		sorted = sorted.map((s, i) => ({ ...s, _originalIdx: i }));

		let filtered = sorted;
		if (searchQuery.trim()) {
			const q = searchQuery.trim().toLowerCase();
			filtered = filtered.filter(s => s.name?.toLowerCase().includes(q));
		}

		if (filtered.length === 0 && searchQuery.trim()) return null;

		const displayList = isExpanded ? filtered : filtered.slice(0, 3);
		const hasMore = filtered.length > 3;

		return (
			<div key={key} className="ai-role-group">
				<div className="ai-role-header" onClick={() => toggleRole(key)}>
					<span className="ai-role-name">{roleName}</span>
					<span className="ai-role-count">{filtered.length}</span>
					<span className={`ai-role-chevron ${isExpanded ? 'expanded' : ''}`}>›</span>
				</div>
				{(isExpanded || !Object.values(expandedRoles).some(Boolean)) && (
					<div className="ai-role-list">
						{displayList.map((suggestion) => {
							const origIdx = suggestion._originalIdx;
							const rank = origIdx < 3 ? RANK_LABELS[origIdx] : null;
							const scorePercent = Math.min(suggestion.fitScore || 0, 100);
							const scoreColor = getScoreColor(scorePercent);

							return (
								<div
									key={suggestion.userId}
									className={`ai-suggestion-card ${rank ? 'ranked' : ''} ${origIdx === 0 ? 'top-pick' : ''}`}
								>
									{rank && <span className={`ai-rank rank-${origIdx + 1}`}>{rank}</span>}
									<div className="ai-suggestion-avatar">
										{suggestion.displayPicture ? (
											<img src={suggestion.displayPicture} alt="" />
										) : (
											<span className="ai-avatar-fallback">
												{(suggestion.name || '?')[0].toUpperCase()}
											</span>
										)}
										<span className={`ai-status-dot ${suggestion.status === 'AVAILABLE' ? 'available' : suggestion.status === 'PARTIALLY_AVAILABLE' ? 'partial' : 'busy'}`} />
									</div>
									<div className="ai-suggestion-info">
										<div className="ai-suggestion-name">{suggestion.name}</div>
										<div className="ai-suggestion-meta">
											<span className={`ai-status-label ${suggestion.status === 'AVAILABLE' ? 'available' : suggestion.status === 'PARTIALLY_AVAILABLE' ? 'partial' : 'busy'}`}>
												{getStatusLabel(suggestion.status)}
											</span>
											{suggestion.events?.length > 0 && (
												<Tooltip
													title={
														<div>
															{suggestion.events.map((e, i) => (
																<div key={i} style={{ fontSize: 11, padding: '2px 0' }}>
																	{e.eventName}{e.eventTime ? ` · ${e.eventTime}` : ''}{e.isFullDay ? ' (Full Day)' : ''}
																</div>
															))}
														</div>
													}
													placement="right"
													getPopupContainer={(trigger) => trigger.parentElement}
												>
													<span className="ai-event-count">{suggestion.events.length} event{suggestion.events.length > 1 ? 's' : ''}</span>
												</Tooltip>
											)}
										</div>
									</div>
									<Tooltip
										title={
											<div className="ai-score-tooltip">
												<div className="ai-score-tooltip-title">{suggestion.name} — {suggestion.fitScore} pts</div>
												{suggestion.fitReasons?.map((reason, i) => (
													<div key={i} className="ai-score-tooltip-reason">• {reason}</div>
												))}
											</div>
										}
										placement="left"
										getPopupContainer={(trigger) => trigger.parentElement}
									>
										<div className="ai-suggestion-score">
											<div className="ai-score-bar">
												<div
													className="ai-score-fill"
													style={{ width: `${scorePercent}%`, background: scoreColor }}
												/>
											</div>
											<span className="ai-score-value" style={{ color: scoreColor }}>
												{suggestion.fitScore}
											</span>
										</div>
									</Tooltip>
								</div>
							);
						})}
						{hasMore && !isExpanded && (
							<div className="ai-show-more" onClick={() => toggleRole(key)}>
								+{filtered.length - 3} more
							</div>
						)}
					</div>
				)}
			</div>
		);
	};

	return (
		<div className="ai-crew-suggestions">
			<div className="ai-suggestions-header">
				<div className="ai-header-left">
					<span className="ai-sparkle">✦</span>
					<span className="ai-header-title">AI Crew Suggestions</span>
					<Tooltip
						title="Suggestions are ranked by availability, skill match, project familiarity, leadership, and workload freshness."
						placement="bottom"
						overlayStyle={{ maxWidth: 260 }}
						getPopupContainer={(trigger) => trigger.parentElement}
					>
						<span className="ai-header-help">?</span>
					</Tooltip>
				</div>
				{loading && (
					<div className="ai-loading">
						<span className="ai-loading-dot" />
						<span className="ai-loading-dot" />
						<span className="ai-loading-dot" />
					</div>
				)}
			</div>
			{!loading && suggestions.length > 0 && (
				<>
					<div className="ai-search-bar">
						<input
							ref={searchRef}
							type="text"
							placeholder="Search crew..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
						/>
					</div>
					<div className="ai-suggestions-body">
						{suggestions.map(renderRoleGroup)}
					</div>
				</>
			)}
		</div>
	);
};

export default CrewSuggestions;
